/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { verifyEmail } from "../api/api";
import PaxiaLogo from "../branding/logo";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("loading"); // loading | success | already | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("No verification token provided.");
      return;
    }

    verifyEmail(token)
      .then((data) => {
        if (data.detail?.toLowerCase().includes("already verified")) {
          setStatus("already");
        } else {
          setStatus("success");
        }
        setMessage(data.detail);
      })
      .catch((err) => {
        setStatus("error");
        setMessage(
          err?.detail ||
            err?.message ||
            "Verification failed. The link may have expired."
        );
      });
  }, [token]);

  const iconMap = {
    loading: (
      <svg className="animate-spin h-10 w-10 text-[#4FA9A1]" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    ),
    success: (
      <svg className="h-12 w-12 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    already: (
      <svg className="h-12 w-12 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    error: (
      <svg className="h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-[#0F1826] font-inter text-white overflow-hidden px-4 sm:px-6">
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-[#0F1826]/90 to-[#0F1826]/90"></div>
      <div className="relative z-20 bg-[#1A2539]/80 backdrop-blur-md p-5 sm:p-8 rounded-xl shadow-lg w-full max-w-[340px] sm:max-w-[384px] text-center">
        <div className="flex flex-col items-center mb-6 space-y-3">
          <img
            src={PaxiaLogo}
            alt="paxia.ai"
            className="h-14 sm:h-16 w-auto object-contain bg-transparent select-none"
          />
          <h1 className="text-2xl font-bold tracking-tight">paxia.ai</h1>
        </div>

        <div className="flex flex-col items-center space-y-4">
          {iconMap[status]}
          <h2 className="text-lg font-semibold">
            {status === "loading" && "Verifying your email..."}
            {status === "success" && "Email Verified!"}
            {status === "already" && "Already Verified"}
            {status === "error" && "Verification Failed"}
          </h2>
          {message && (
            <p className="text-sm text-gray-400">{message}</p>
          )}
          {status !== "loading" && (
            <Link
              to="/login"
              className="mt-2 bg-[#4FA9A1] hover:bg-[#3e938b] transition-colors text-[#0F1826] font-semibold py-2 px-6 rounded-lg text-sm"
            >
              Go to Login
            </Link>
          )}
          {status === "error" && (
            <Link
              to="/register"
              className="text-[#4FA9A1] hover:underline text-sm"
            >
              Register again
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
